import { DatePipe } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableModule } from '@angular/material/table';
import { firstValueFrom } from 'rxjs';
import { DIALOG_WIDTH_SM, SNACKBAR_DURATION_ERROR, SNACKBAR_DURATION_SUCCESS, USERS_PAGE_SIZE } from '../../app.constants';
import { ConfirmDialogComponent } from '../../main/confirm-dialog/confirm-dialog.component';
import { AdminUserGroupsDialogComponent, AdminUserGroupsDialogData } from '../admin-user-groups-dialog/admin-user-groups-dialog.component';
import { AdminUser, AdminUsersService } from '../admin-users.service';

@Component({
	selector: 'app-admin-users',
	imports: [DatePipe, MatProgressSpinnerModule, MatTableModule, MatButtonModule, MatIconModule, MatMenuModule, MatFormFieldModule, MatInputModule],
	templateUrl: './admin-users.component.html',
	styleUrl: './admin-users.component.scss',
})
export class AdminUsersComponent {
	private readonly usersService = inject(AdminUsersService);
	private readonly snackBar = inject(MatSnackBar);
	private readonly dialog = inject(MatDialog);

	pagedUsers = signal<AdminUser[]>([]);
	usersLoaded = signal(false);
	usersLoading = signal(false);
	currentPage = signal(1);
	hasNextPage = signal(false);
	hasPrevPage = computed(() => this.currentPage() > 1);
	searchQuery = signal('');

	// Index = page number (0-based). Stores the nextToken needed to fetch that page.
	private readonly pageTokens: (string | undefined)[] = [undefined];
	private readonly pageCache = new Map<number, AdminUser[]>();
	private searchTimeout: ReturnType<typeof setTimeout> | null = null;

	readonly userColumns = ['email', 'enabled', 'creationDate', 'actions'];

	constructor() {
		this.loadPage(0);
	}

	private async loadPage(pageIndex: number) {
		const queryAtStart = this.searchQuery();

		const cached = this.pageCache.get(pageIndex);
		if (cached) {
			this.pagedUsers.set(cached);
			this.currentPage.set(pageIndex + 1);
			this.hasNextPage.set(this.pageTokens[pageIndex + 1] !== undefined);
			return;
		}

		this.usersLoading.set(true);
		try {
			const token = this.pageTokens[pageIndex];
			const page = await firstValueFrom(this.usersService.getUsers(USERS_PAGE_SIZE, token, queryAtStart || undefined));

			if (this.searchQuery() !== queryAtStart) return;

			this.pageCache.set(pageIndex, page.users);
			if (page.nextToken && this.pageTokens[pageIndex + 1] === undefined) {
				this.pageTokens[pageIndex + 1] = page.nextToken;
			}

			this.pagedUsers.set(page.users);
			this.currentPage.set(pageIndex + 1);
			this.hasNextPage.set(!!page.nextToken);
		} catch {
			this.snackBar.open(
				$localize`:@@FailedToLoadUsers:Failed to load users`,
				undefined,
				{ duration: SNACKBAR_DURATION_ERROR }
			);
		} finally {
			this.usersLoaded.set(true);
			this.usersLoading.set(false);
		}
	}

	nextPage() {
		if (this.hasNextPage()) this.loadPage(this.currentPage());
	}

	prevPage() {
		if (this.hasPrevPage()) this.loadPage(this.currentPage() - 2);
	}

	onSearchInput(value: string): void {
		this.searchQuery.set(value);
		if (this.searchTimeout) clearTimeout(this.searchTimeout);
		if (!value) {
			this.resetAndLoad();
			return;
		}
		this.searchTimeout = setTimeout(() => this.resetAndLoad(), 400);
	}

	private resetAndLoad(): void {
		this.pageTokens.splice(0, this.pageTokens.length, undefined);
		this.pageCache.clear();
		this.loadPage(0);
	}

	openGroupsDialog(user: AdminUser) {
		this.dialog.open(AdminUserGroupsDialogComponent, {
			width: DIALOG_WIDTH_SM,
			data: { userId: user.userId, email: user.email } satisfies AdminUserGroupsDialogData,
		});
	}

	async openDeleteDialog(user: AdminUser) {
		const ref = this.dialog.open(ConfirmDialogComponent, {
			width: DIALOG_WIDTH_SM,
			data: {
				title: $localize`:@@DeleteUserTitle:Delete user?`,
				message: $localize`:@@DeleteUserMessage:Are you sure you want to delete "${user.email}"?`,
				confirmText: $localize`:@@Delete:Delete`,
				cancelText: $localize`:@@Cancel:Cancel`,
			},
		});

		const confirmed = await firstValueFrom(ref.afterClosed());
		if (confirmed !== true) return;

		const result = await firstValueFrom(this.usersService.deleteUser(user.userId));
		if (result.success) {
			this.snackBar.open(
				$localize`:@@UserDeletedSuccessfully:User deleted successfully`,
				$localize`:@@Close:Close`,
				{ duration: SNACKBAR_DURATION_SUCCESS, panelClass: ['snackbar-success'] }
			);
			this.pagedUsers.update(users => users.filter(u => u.userId !== user.userId));
			const pageIndex = this.currentPage() - 1;
			const cached = this.pageCache.get(pageIndex);
			if (cached) this.pageCache.set(pageIndex, cached.filter(u => u.userId !== user.userId));
		} else {
			this.snackBar.open(
				$localize`:@@FailedWithMessage:Failed: ${result.errorMessage}`,
				$localize`:@@Close:Close`,
				{ duration: SNACKBAR_DURATION_ERROR, panelClass: ['snackbar-error'] }
			);
		}
	}
}
